import React, { useState, useEffect, useContext, useCallback } from "react";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { Button, Badge, Offcanvas, OffcanvasHeader, OffcanvasBody } from "reactstrap";
import { PostData } from "../../../services/FetchData";
import { ProductContext } from "../../../store";
import { toast } from "react-toastify";
import Cookies from "js-cookie";
import DetailSkeleton from "../../../components/Skeleton/DetailSkeleton";
import LiveChatTab from "../../../components/AIAssistant/LiveChatTab";
import OfflineLeadsTab from "../../../components/AIAssistant/OfflineLeadsTab";
import { parseAssistantMarkdown, ProductSlider } from "../../../components/AIAssistant/conversationDisplay";
import { mergeConversationThread } from "../../../services/liveChatUtils";
import { isLiveChatEnabled } from "../../../components/AiVisibility/websiteKeysUtils";
import { format } from "date-fns";

const AIAssistantConversations = () => {
	const { currentWebsiteId, websiteData } = useContext(ProductContext);
	const websiteId = currentWebsiteId || Cookies.get("_website_id");
	const siteRow = websiteId ? websiteData?.[websiteId] ?? websiteData?.[Number(websiteId)] : null;
	const websiteBaseUrl = siteRow?.websiteURL != null ? String(siteRow.websiteURL).trim() : "";

	const [sessionsLoader, setSessionsLoader] = useState(true);
	const [sessions, setSessions] = useState([]);
	const [selectedSession, setSelectedSession] = useState(null);
	const [turnsLoader, setTurnsLoader] = useState(false);
	const [turns, setTurns] = useState([]);
	const [conversationTab, setConversationTab] = useState("bot");
	const [liveChatEnabled, setLiveChatEnabled] = useState(false);
	const [filterOpen, setFilterOpen] = useState(false);
	const [draftSearch, setDraftSearch] = useState("");
	const [committedSearch, setCommittedSearch] = useState("");

	const activeFilterCount = committedSearch ? 1 : 0;

	const openFilterPanel = useCallback(() => {
		setDraftSearch(committedSearch);
		setFilterOpen(true);
	}, [committedSearch]);

	const applyFilters = useCallback(() => {
		setCommittedSearch(draftSearch.trim());
		setFilterOpen(false);
	}, [draftSearch]);

	const clearFilters = useCallback(() => {
		setDraftSearch("");
		setCommittedSearch("");
		setFilterOpen(false);
	}, []);

	useEffect(() => {
		if (!websiteId) {
			setLiveChatEnabled(false);
			setConversationTab("bot");
			return;
		}

		let cancelled = false;
		const fetchWebsiteKeys = async () => {
			try {
				const response = await PostData("ms1", "api/v1/website/getWebsiteKeys", {
					websiteId,
					productType: "aiassistant",
				});
				if (cancelled) return;
				const websiteKeys = response?.status === "success" ? response?.data?.websiteKeys : [];
				const enabled = isLiveChatEnabled(websiteKeys);
				setLiveChatEnabled(enabled);
				if (!enabled) {
					setConversationTab("bot");
				}
			} catch (error) {
				if (!cancelled) {
					console.error("Error fetching website keys:", error);
					setLiveChatEnabled(false);
					setConversationTab("bot");
				}
			}
		};
		fetchWebsiteKeys();
		return () => {
			cancelled = true;
		};
	}, [websiteId]);

	useEffect(() => {
		if (!websiteId) {
			setSessionsLoader(false);
			return;
		}
		const fetchSessions = async () => {
			setSessionsLoader(true);
			try {
				const payload = {
					websiteId,
					...(committedSearch ? { q: committedSearch } : {}),
				};
				const response = await PostData("ms1", "api/v1/aiassistant/shopping-assistant-sessions", payload);
				if (response?.status === "success" && response?.data?.sessions) {
					const list = response.data.sessions;
					setSessions(list);
					if (list.length > 0) {
						setSelectedSession((prev) => {
							if (!prev || !list.some((s) => s.sessionId === prev.sessionId)) {
								return list[0];
							}
							return prev;
						});
					} else {
						setSelectedSession(null);
					}
				} else {
					setSessions([]);
					setSelectedSession(null);
				}
			} catch (error) {
				console.error("Error fetching sessions:", error);
				toast.error("Failed to load conversations");
				setSessions([]);
			} finally {
				setSessionsLoader(false);
			}
		};
		fetchSessions();
	}, [websiteId, committedSearch]);

	useEffect(() => {
		if (!websiteId || !selectedSession?.sessionId) {
			setTurns([]);
			return;
		}
		const fetchConversation = async () => {
			setTurnsLoader(true);
			try {
				const response = await PostData("ms1", "api/v1/aiassistant/shopping-assistant-conversation", {
					websiteId,
					sessionId: selectedSession.sessionId,
					visitorId: selectedSession.visitorId || undefined,
				});
				if (response?.status === "success") {
					setTurns(Array.isArray(response?.data?.turns) ? response.data.turns : []);
				} else {
					setTurns([]);
				}
			} catch (error) {
				console.error("Error fetching conversation:", error);
				toast.error("Failed to load conversation");
				setTurns([]);
			} finally {
				setTurnsLoader(false);
			}
		};
		fetchConversation();
	}, [websiteId, selectedSession?.sessionId, selectedSession?.visitorId]);

	const mergedThread = mergeConversationThread(turns, []);

	const getInitials = (session) => {
		if (session?.visitorId && String(session.visitorId).length >= 2) {
			return String(session.visitorId).slice(0, 2).toUpperCase();
		}
		return "?";
	};

	if (!websiteId) {
		return <DetailSkeleton />;
	}

	return (
		<div className="pageContent">
			<style>{`
				.sa-conversation-shell {
					display: flex;
					flex-direction: column;
					height: calc(100vh - 180px);
					min-height: 400px;
					margin-top: 1rem;
				}
				.sa-conversation-tabs {
					display: flex;
					align-items: flex-end;
					flex-shrink: 0;
					gap: 0;
					padding: 0;
				}
				.sa-conversation-tabs__btn {
					padding: 0.4rem 1.1rem;
					border: 1px solid #dee2e6;
					border-bottom: none;
					border-radius: 6px 6px 0 0;
					background: #f8f9fa;
					color: #6c757d;
					font-weight: 500;
					font-size: 14px;
					cursor: pointer;
					line-height: 1.4;
					margin-bottom: -1px;
					position: relative;
				}
				.sa-conversation-tabs__btn + .sa-conversation-tabs__btn {
					margin-left: -1px;
				}
				.sa-conversation-tabs__btn:hover:not(.sa-conversation-tabs__btn--active) {
					color: #29394e;
					background: #f1f3f5;
				}
				.sa-conversation-tabs__btn--active {
					background: #fff;
					color: #005eff;
					font-weight: 600;
					z-index: 1;
					border-bottom: 1px solid #fff;
				}
				.sa-conversation-panel {
					flex: 1;
					min-height: 0;
					border: 1px solid #dee2e6;
					border-radius: 0 6px 6px 6px;
					overflow: hidden;
					background: #fff;
				}
				.sa-conversation-panel--no-tabs {
					border-radius: 6px;
				}
			`}</style>
			<div className="pageHeader">
				<div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
					<h2 className="pageTitle mb-0">Conversations</h2>
					{(!liveChatEnabled || conversationTab === "bot") ? (
						<Button color="primary" outline size="sm" className="d-inline-flex align-items-center gap-2" onClick={openFilterPanel}>
							<i className="fa fa-filter" aria-hidden="true" />
							<span>Filter</span>
							{activeFilterCount > 0 ? (
								<Badge color="primary" pill className="ms-1">
									{activeFilterCount}
								</Badge>
							) : null}
						</Button>
					) : null}
				</div>
			</div>

			<Offcanvas direction="end" isOpen={filterOpen} toggle={() => setFilterOpen(false)} style={{ maxWidth: 400, width: "100%" }}>
				<OffcanvasHeader toggle={() => setFilterOpen(false)}>Filter conversations</OffcanvasHeader>
				<OffcanvasBody className="d-flex flex-column gap-3 pt-0">
					<div>
						<label className="form-label small text-muted mb-1">Message search</label>
						<input
							type="search"
							className="form-control form-control-sm"
							placeholder="Words in any question or answer…"
							value={draftSearch}
							onChange={(e) => setDraftSearch(e.target.value)}
							aria-label="Search conversation messages"
						/>
					</div>
					<div className="d-flex flex-wrap gap-2 mt-auto pt-2 border-top">
						<Button color="primary" size="sm" onClick={applyFilters}>
							Apply filters
						</Button>
						<Button color="link" size="sm" className="text-decoration-none px-2" onClick={clearFilters}>
							Clear all
						</Button>
					</div>
				</OffcanvasBody>
			</Offcanvas>

			<div className="sa-conversation-shell">
				{liveChatEnabled ? (
					<div className="sa-conversation-tabs" role="tablist" aria-label="Conversation type">
						<button
							type="button"
							role="tab"
							aria-selected={conversationTab === "bot"}
							className={`sa-conversation-tabs__btn${conversationTab === "bot" ? " sa-conversation-tabs__btn--active" : ""}`}
							onClick={() => setConversationTab("bot")}
						>
							Bot
						</button>
						<button
							type="button"
							role="tab"
							aria-selected={conversationTab === "live"}
							className={`sa-conversation-tabs__btn${conversationTab === "live" ? " sa-conversation-tabs__btn--active" : ""}`}
							onClick={() => setConversationTab("live")}
						>
							Live
						</button>
						<button
							type="button"
							role="tab"
							aria-selected={conversationTab === "offline"}
							className={`sa-conversation-tabs__btn${conversationTab === "offline" ? " sa-conversation-tabs__btn--active" : ""}`}
							onClick={() => setConversationTab("offline")}
						>
							Offline
						</button>
					</div>
				) : null}

				<div
					className={`sa-conversation-panel${liveChatEnabled ? "" : " sa-conversation-panel--no-tabs"}`}
					role="tabpanel"
				>
			<div
				className="chat-container"
				style={{
					display: !liveChatEnabled || conversationTab === "bot" ? "flex" : "none",
					// bot panel hidden when live or offline tab active
					height: "100%",
					minHeight: 0,
					overflow: "hidden",
					backgroundColor: "#fff",
				}}
			>
				<div
					style={{
						width: 320,
						minWidth: 320,
						borderRight: "1px solid #dee2e6",
						display: "flex",
						flexDirection: "column",
						backgroundColor: "#f8f9fa",
					}}
				>
					{sessionsLoader ? (
						<div style={{ overflowY: "auto", flex: 1, padding: "8px 0" }}>
							{[1, 2, 3, 4, 5].map((i) => (
								<div key={i} className="d-flex align-items-start gap-2" style={{ padding: "12px 14px", borderBottom: "1px solid #eee" }}>
									<Skeleton circle width={40} height={40} />
									<div style={{ flex: 1, minWidth: 0 }}>
										<Skeleton height={14} count={2} style={{ marginBottom: 4 }} />
										<Skeleton height={11} width={60} />
									</div>
								</div>
							))}
						</div>
					) : sessions.length === 0 ? (
						<div className="p-4 text-muted text-center small">
							{committedSearch ? "No conversations match your filters" : "No conversations yet"}
						</div>
					) : (
						<div style={{ overflowY: "auto", flex: 1 }}>
							{sessions.map((session) => {
								const isSelected = selectedSession && selectedSession.sessionId === session.sessionId;
								return (
									<div
										key={session.sessionId}
										onClick={() => setSelectedSession(session)}
										style={{
											padding: "12px 14px",
											borderBottom: "1px solid #eee",
											cursor: "pointer",
											backgroundColor: isSelected ? "#e7f1ff" : "transparent",
										}}
									>
										<div className="d-flex align-items-start gap-2">
											<div
												style={{
													width: 40,
													height: 40,
													borderRadius: "50%",
													background: "#5c50e6",
													color: "#fff",
													display: "flex",
													alignItems: "center",
													justifyContent: "center",
													fontSize: 14,
													fontWeight: 600,
													flexShrink: 0,
												}}
											>
												{getInitials(session)}
											</div>
											<div style={{ flex: 1, minWidth: 0 }}>
												<div
													style={{
														fontSize: 13,
														fontWeight: 500,
														color: "#333",
														overflow: "hidden",
														textOverflow: "ellipsis",
														display: "-webkit-box",
														WebkitLineClamp: 2,
														WebkitBoxOrient: "vertical",
														lineHeight: 1.3,
													}}
												>
													{session.firstMessage || "No message"}
												</div>
												<div
													className="d-flex align-items-center gap-2 flex-wrap"
													style={{
														fontSize: 11,
														color: "#adb5bd",
														marginTop: 4,
													}}
												>
													<span>{session.dateTime ? format(new Date(session.dateTime), "MM/dd/yyyy") : ""}</span>
													{session.hasLiveHandoff || session.liveSessionUuid ? (
														<Badge color="info" pill style={{ fontSize: 10 }}>
															Live handoff
														</Badge>
													) : null}
												</div>
											</div>
										</div>
									</div>
								);
							})}
						</div>
					)}
				</div>

				<div style={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0 }}>
					{!selectedSession ? (
						<div className="d-flex align-items-center justify-content-center text-muted" style={{ flex: 1 }}>
							Select a conversation
						</div>
					) : (
						<>
							<div
								style={{
									padding: "12px 16px",
									borderBottom: "1px solid #dee2e6",
									backgroundColor: "#fff",
								}}
							>
								<h6
									className="mb-0"
									style={{
										fontSize: 14,
										fontWeight: 600,
										color: "#333",
										overflow: "hidden",
										textOverflow: "ellipsis",
										display: "-webkit-box",
										WebkitLineClamp: 2,
										WebkitBoxOrient: "vertical",
									}}
								>
									{selectedSession.firstMessage || "Conversation"}
								</h6>
							</div>

							<style>{`
								.cb-sa-conv-msg-text ul { list-style-type: disc; padding-left: 1.5em; margin: 0.5em 0; }
								.cb-sa-conv-msg-text ol { list-style-type: decimal; padding-left: 1.5em; margin: 0.5em 0; }
								.cb-sa-conv-msg-text li { margin: 0.25em 0; font-size: 14px; }
							`}</style>
							<div
								style={{
									flex: 1,
									overflowY: "auto",
									padding: 16,
									backgroundColor: "#f8f9fa",
								}}
							>
								{turnsLoader ? (
									<div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
										<div className="d-flex" style={{ justifyContent: "flex-start" }}>
											<div
												style={{
													maxWidth: "75%",
													padding: "10px 14px",
													borderRadius: 12,
													backgroundColor: "#fff",
													border: "1px solid #dee2e6",
												}}
											>
												<Skeleton height={14} width={120} />
											</div>
										</div>
										<div className="d-flex" style={{ justifyContent: "flex-end" }}>
											<div
												style={{
													maxWidth: "85%",
													padding: "10px 14px",
													borderRadius: 12,
													backgroundColor: "#e9ecef",
												}}
											>
												<Skeleton height={14} width={180} />
												<Skeleton height={14} width={160} style={{ marginTop: 6 }} />
											</div>
										</div>
									</div>
								) : mergedThread.length === 0 ? (
									<div className="text-muted text-center py-5">No messages in this conversation</div>
								) : (
									mergedThread.map((item, idx) => {
										if (item.kind === "live_system") {
											return (
												<div key={`sys-${idx}`} className="text-center text-muted small my-3">
													{item.text}
												</div>
											);
										}
										if (item.kind === "live_message") {
											const isExec = item.senderType === "executive";
											const isCustomer = item.senderType === "customer";
											return (
												<div
													key={`live-${idx}`}
													className="d-flex mb-3"
													style={{ justifyContent: isExec ? "flex-end" : "flex-start" }}
												>
													<div
														style={{
															maxWidth: "85%",
															padding: "10px 14px",
															borderRadius: 12,
															backgroundColor: isExec ? "#d1e7dd" : isCustomer ? "#fff" : "#e9ecef",
															border: isCustomer ? "1px solid #dee2e6" : "none",
														}}
													>
														<div style={{ fontSize: 14, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{item.text}</div>
														<div className="text-muted mt-1" style={{ fontSize: 11 }}>
															{isExec ? "Executive" : isCustomer ? "Customer" : item.senderType}
															{item.timestamp && ` · ${format(new Date(item.timestamp), "MM/dd/yyyy h:mm a")}`}
														</div>
													</div>
												</div>
											);
										}
										if (item.kind === "ai_turn_question") {
											return (
												<div key={`q-${idx}`} className="d-flex mb-3" style={{ justifyContent: "flex-start" }}>
													<div
														style={{
															maxWidth: "75%",
															padding: "10px 14px",
															borderRadius: 12,
															backgroundColor: "#fff",
															border: "1px solid #dee2e6",
															boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
														}}
													>
														<div style={{ fontSize: 14, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{item.text}</div>
														<div className="text-muted mt-1" style={{ fontSize: 11 }}>
															User
															{item.timestamp && ` · ${format(new Date(item.timestamp), "MM/dd/yyyy h:mm a")}`}
														</div>
													</div>
												</div>
											);
										}
										const turn = turns[item.turnIdx];
										return (
											<div key={`a-${idx}`} className="d-flex mb-3" style={{ justifyContent: "flex-end" }}>
												<div
													style={{
														maxWidth: "85%",
														padding: "10px 14px",
														borderRadius: 12,
														backgroundColor: "#e9ecef",
													}}
												>
													<div
														className="cb-sa-conv-msg-text"
														style={{ fontSize: 14, wordBreak: "break-word" }}
														dangerouslySetInnerHTML={{ __html: parseAssistantMarkdown(item.text || "") }}
													/>
													{turn ? <ProductSlider products={turn.products} websiteBaseUrl={websiteBaseUrl} /> : null}
													<div className="text-muted mt-1" style={{ fontSize: 11 }}>
														Assistant
														{item.timestamp && ` · ${format(new Date(item.timestamp), "MM/dd/yyyy h:mm a")}`}
													</div>
												</div>
											</div>
										);
									})
								)}
							</div>
						</>
					)}
				</div>
			</div>
					{liveChatEnabled ? (
						<div
							style={{
								display: conversationTab === "live" ? "block" : "none",
								height: "100%",
								minHeight: 0,
							}}
						>
							<LiveChatTab websiteId={websiteId} active={conversationTab === "live"} embedded />
						</div>
					) : null}
					{liveChatEnabled ? (
						<div
							style={{
								display: conversationTab === "offline" ? "block" : "none",
								height: "100%",
								minHeight: 0,
							}}
						>
							<OfflineLeadsTab websiteId={websiteId} active={conversationTab === "offline"} />
						</div>
					) : null}
				</div>
			</div>
		</div>
	);
};

export default AIAssistantConversations;
